import { Component } from '@theme/component';

/**
 * "Notify me when back in stock" — Klaviyo Back in Stock via the public Client API.
 *
 * Rendered by snippets/notify-me.liquid as the second button of the product action row,
 * only when the selected variant is out of stock and a Klaviyo public key is configured
 * in theme settings. Two shapes share this class:
 *   - one-click: a hidden email (signed-in customer) + the button itself is `ref="submit"`.
 *   - dialog: the visible button opens a <dialog-component>; inside it an email input and
 *     a `ref="submit"` button.
 * Either way, pressing submit posts ONE subscription: the email against the Shopify
 * variant, which Klaviyo knows through its Shopify catalogue integration as
 * `$shopify:::$default:::<variant id>`. Klaviyo then watches Shopify inventory and fires
 * its Back in Stock flow on 0 -> >0; nothing of ours is involved after this request.
 *
 * Endpoint + payload: Klaviyo Client API, "Create Client Back In Stock Subscription".
 * Revision pinned; bump deliberately. A 202 with no body is success.
 *
 * States live ON THE BUTTON (no separate confirmation line — it sat orphaned under the
 * right-hand column of the trade action row):
 *   - subscribed: label -> "We'll email you when it's back", bell -> tick, secondary
 *     colours, inert. Remembered in localStorage per customer + variant for 60 days, so a
 *     returning trade buyer sees they already asked and can't double-subscribe. Per
 *     browser only — Klaviyo's client API has no "am I subscribed?" lookup, and the theme
 *     has no server to ask. Good enough: Klaviyo dedupes the profile+variant server-side
 *     anyway, this is purely so the button tells the truth.
 *   - error: error copy in the button for a few seconds, then the label returns.
 * The dialog shape keeps its in-dialog status line (there is room for it there) and flips
 * its trigger button to the subscribed state as well.
 *
 * No <form> element on purpose: the trade instance sits INSIDE the product form, and
 * nested forms are invalid HTML. Lives inside a <variant-content> region, so a variant
 * change morphs the whole element from the freshly fetched page with the new variant id.
 */
class NotifyMeForm extends Component {
  /**
   * Own hooks are `data-ref`, NOT the theme's `ref`: Component scopes `ref` to the closest
   * component, and in the dialog shape our input/submit/message sit inside a nested
   * <dialog-component>, which claims them — this element's required-ref check then throws
   * on connect and nothing works. (Found 2026-09-16, logged-out test.)
   * @param {string} name
   * @returns {HTMLElement | null}
   */
  #el(name) {
    return this.querySelector(`[data-ref="${name}"]`);
  }

  /** @returns {HTMLButtonElement} */
  get #submit() {
    return /** @type {HTMLButtonElement} */ (this.#el('submit'));
  }

  /** @returns {HTMLInputElement} */
  get #email() {
    return /** @type {HTMLInputElement} */ (this.#el('email'));
  }

  static API = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  static REVISION = '2025-01-15';
  static STORAGE_PREFIX = 'frelan:notify-me:';
  static REMEMBER_MS = 60 * 24 * 60 * 60 * 1000;
  static ERROR_MS = 4000;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #errorTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#submit.addEventListener('click', this.#onSubmit);
    this.#email.addEventListener('keydown', this.#onKeydown);
    if (this.#remembered()) this.#markSubscribed();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#submit.removeEventListener('click', this.#onSubmit);
    this.#email.removeEventListener('keydown', this.#onKeydown);
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#onSubmit(event);
    }
  };

  /** @param {Event} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const email = this.#email;
    const submit = this.#submit;
    const address = email.value.trim();
    if (!address || (email.type === 'email' && !email.checkValidity())) {
      if (email.type === 'email') email.reportValidity();
      return;
    }
    const companyId = this.dataset.companyId;
    const variantId = this.dataset.variantId;
    if (!companyId || !variantId) return;

    submit.disabled = true;
    this.#show('', '');
    this.#setButtonState(null);
    try {
      const response = await fetch(`${NotifyMeForm.API}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: NotifyMeForm.REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: address } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` },
              },
            },
          },
        }),
      });
      if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
      this.#remember();
      this.#markSubscribed();
      // Dialog shape: confirm inside the dialog too and hide the input row.
      this.#show(this.#copy('successText'), 'success');
      email.closest('.notify-me__row')?.setAttribute('hidden', '');
    } catch (error) {
      console.error('[notify-me]', error);
      submit.disabled = false;
      if (this.#isOneClick()) {
        this.#setButtonState('error', this.#copy('errorText'));
        clearTimeout(this.#errorTimer);
        this.#errorTimer = setTimeout(() => this.#setButtonState(null), NotifyMeForm.ERROR_MS);
      } else {
        this.#show(this.#copy('errorText'), 'error');
      }
    }
  };

  /** One-click shape = the visible action button is the submit itself. */
  #isOneClick() {
    return !this.#el('trigger');
  }

  /** The button the visitor sees in the action row (submit in one-click, trigger in dialog). */
  #actionButton() {
    return /** @type {HTMLButtonElement} */ (this.#el('trigger') ?? this.#submit);
  }

  /**
   * @param {'subscribed' | 'error' | null} state
   * @param {string} [text] label to show; omitted = restore the original label
   */
  #setButtonState(state, text) {
    const button = this.#actionButton();
    const label = this.#el('buttonText');
    if (label) {
      if (label.dataset.original === undefined) label.dataset.original = label.textContent ?? '';
      label.textContent = text ?? label.dataset.original;
    }
    if (state) button.setAttribute('data-state', state);
    else button.removeAttribute('data-state');
  }

  #markSubscribed() {
    this.#setButtonState('subscribed', this.#copy('subscribedText'));
    const button = this.#actionButton();
    button.setAttribute('aria-disabled', 'true');
    button.disabled = true;
  }

  #storageKey() {
    return `${NotifyMeForm.STORAGE_PREFIX}${this.dataset.customerId ?? 'guest'}:${this.dataset.variantId}`;
  }

  #remembered() {
    try {
      const raw = localStorage.getItem(this.#storageKey());
      if (!raw) return false;
      const at = Number(raw);
      if (!Number.isFinite(at) || Date.now() - at > NotifyMeForm.REMEMBER_MS) {
        localStorage.removeItem(this.#storageKey());
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  #remember() {
    try {
      localStorage.setItem(this.#storageKey(), String(Date.now()));
    } catch {
      /* private mode / storage blocked — the button still confirms for this page view */
    }
  }

  /** @param {'successText' | 'errorText' | 'subscribedText'} ref */
  #copy(ref) {
    const template = /** @type {HTMLTemplateElement | null} */ (this.#el(ref));
    if (!template) return '';
    return template.content.textContent?.trim() ?? '';
  }

  /**
   * @param {string} text
   * @param {string} state
   */
  #show(text, state) {
    const message = this.#el('message');
    if (!message) return; // one-click shape has no status line
    message.textContent = text;
    message.hidden = !text;
    if (state) message.setAttribute('data-state', state);
    else message.removeAttribute('data-state');
  }
}

if (!customElements.get('notify-me-form')) {
  customElements.define('notify-me-form', NotifyMeForm);
}
